import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

const v5OverlayAlpha = (elevation: number): number => {
  const value =
    elevation < 1
      ? 5.11916 * elevation ** 2
      : 4.5 * Math.log(elevation + 1) + 2;
  return Number((value / 100).toFixed(2));
};

const v5SelectFocus = ({ theme }: { theme: Theme }) => ({
  '&:focus': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.05)'
        : 'rgba(255, 255, 255, 0.05)',
  },
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const MATERIAL_5_8_LOOK: Components<Theme> = {
  MuiAlert: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        const tone =
          theme.palette[ownerState.color ?? ownerState.severity ?? 'success'];
        if (theme.palette.mode === 'light' && ownerState.variant !== 'filled') {
          return { '& .MuiAlert-icon': { color: tone.light } };
        }
        if (theme.palette.mode === 'dark' && ownerState.variant === 'filled') {
          return { color: theme.palette.getContrastText(tone.dark) };
        }
        return {};
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          padding: '7.5px 4px 7.5px 6px',
        },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input':
          {
            padding: '2.5px 4px 2.5px 6px',
          },
      },
      inputRoot: {
        flexWrap: 'wrap',
      },
      endAdornment: {
        top: 'calc(50% - 14px)',
        transform: 'none',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) =>
        ownerState.loading == null
          ? {
              transition: theme.transitions.create(
                ['background-color', 'box-shadow', 'border-color', 'color'],
                { duration: theme.transitions.duration.short }
              ),
            }
          : {},
    },
  },
  MuiCardActionArea: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        lineHeight: 'inherit',
        ...(!ownerState.clickable && { cursor: 'default' }),
      }),
      label: ({ ownerState }) => {
        if (ownerState.variant !== 'outlined') {
          return {};
        }
        const padding = ownerState.size === 'small' ? 8 : 12;
        return { paddingLeft: padding, paddingRight: padding };
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        outline: 'none',
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        '&.Mui-error:after': {
          transform: 'scaleX(1)',
        },
      },
    },
  },
  MuiInput: {
    styleOverrides: {
      root: {
        '&.Mui-error:after': {
          transform: 'scaleX(1)',
        },
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        minWidth: 'auto',
        ...(ownerState.container &&
          isRecord(ownerState.size) &&
          Object.keys(ownerState.size).length === 0 && { width: '100%' }),
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        height: '0.01em',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.variant === 'outlined' && ownerState.shrink
          ? { maxWidth: 'calc(133% - 24px)' }
          : {},
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        maxHeight: 'calc(100% - 32px)',
      },
    },
  },
  MuiNativeSelect: {
    styleOverrides: {
      select: v5SelectFocus,
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        if (
          theme.palette.mode !== 'dark' ||
          ownerState.variant !== 'elevation'
        ) {
          return {};
        }
        const overlay = alpha(
          '#fff',
          v5OverlayAlpha(ownerState.elevation ?? 1)
        );
        return { backgroundImage: `linear-gradient(${overlay}, ${overlay})` };
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: v5SelectFocus,
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      pulse: {
        animationDuration: '1.5s',
      },
      wave: {
        '&::after': {
          animationDuration: '1.6s',
        },
      },
    },
  },
  MuiSwitch: {
    defaultProps: {
      slotProps: { input: { role: undefined } },
    },
    styleOverrides: {
      thumb: {
        border: 0,
      },
      track: {
        border: 0,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ ownerState }) =>
        ownerState.variant === 'inherit' ? { font: 'revert' } : {},
    },
  },
};
